import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { MODULE_CATALOG, MODULE_GROUPS, ROLE_LABELS, moduleAccessibleFor } from "../lib/modules";
import {
  LayoutDashboard, LogOut, ChevronDown, User as UserIcon, Clock, BookUser, ListChecks,
  Laptop, TrendingUp, Menu, PanelLeftClose, PanelLeftOpen, PlayCircle, ShieldCheck,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import NotificationBell from "./NotificationBell";
import TicketBookingBell from "./TicketBookingBell";
import { MasterAdminEditRequestsButton } from "./EditAccessRequest";
import AnnouncementsBell from "./AnnouncementsBell";
import NotificationHighlighter from "./NotificationHighlighter";
import DailyStatusPills from "./DailyStatusPills";
import PolicyAckGate from "./documents/PolicyAckGate";
// MandatoryPasswordChange is rendered by ProtectedRoute so non-Layout routes
// (e.g. /my-onboarding) also get the SEC-001 hard-gate dialog.
import { ThemeToggle } from "./ThemeToggle";
import ViewModeToggle, { useGlobalViewMode } from "./ViewModeToggle";
import OnboardingTour, { replayOnboardingTour } from "./OnboardingTour";
import ActivityHeartbeat from "./ActivityHeartbeat";
import FloatingChatBubble from "./FloatingChatBubble";
import MandatoryAnnouncementBanner from "./MandatoryAnnouncementBanner";

// ---------------------------------------------------------------------------
// v36 sidebar — glowing icons, no boxes, mirror sheen on hover, glassy depth.
// Each MODULE gets its own deterministic vibrant tint (hashed from its key),
// so when clicked the icon's colour identifies the module. The group fallback
// is still used for the section header dot + colour grouping logic.
// ---------------------------------------------------------------------------
const GROUP_TONE = {
  Overview:      { dot: "bg-blue-500",    text: "text-blue-700",    hex: "#3b82f6" },
  Workspace:     { dot: "bg-indigo-500",  text: "text-indigo-700",  hex: "#6366f1" },
  HR:            { dot: "bg-emerald-500", text: "text-emerald-700", hex: "#10b981" },
  Sales:         { dot: "bg-sky-500",     text: "text-sky-700",     hex: "#0ea5e9" },
  CRM:           { dot: "bg-cyan-500",    text: "text-cyan-700",    hex: "#06b6d4" },
  Documentation: { dot: "bg-violet-500",  text: "text-violet-700",  hex: "#8b5cf6" },
  Accounts:      { dot: "bg-fuchsia-500", text: "text-fuchsia-700", hex: "#d946ef" },
  Dispatch:      { dot: "bg-amber-500",   text: "text-amber-700",   hex: "#f59e0b" },
  IT:            { dot: "bg-rose-500",    text: "text-rose-700",    hex: "#f43f5e" },
  Trainers:      { dot: "bg-lime-500",    text: "text-lime-700",    hex: "#84cc16" },
  Other:         { dot: "bg-slate-400",   text: "text-slate-600",   hex: "#64748b" },
};

function tone(group) { return GROUP_TONE[group] || GROUP_TONE.Other; }

// 24-colour vibrant palette so every module key hashes to a distinct, lively
// tint. Each menu item then carries its own personality.
const MODULE_PALETTE = [
  "#ef4444", "#f97316", "#f59e0b", "#eab308",
  "#84cc16", "#22c55e", "#10b981", "#14b8a6",
  "#06b6d4", "#0ea5e9", "#3b82f6", "#6366f1",
  "#8b5cf6", "#a855f7", "#d946ef", "#ec4899",
  "#f43f5e", "#0d9488", "#9333ea", "#db2777",
  "#16a34a", "#0891b2", "#4f46e5", "#dc2626",
];

function tintForKey(key) {
  if (!key) return GROUP_TONE.Other.hex;
  let h = 0;
  for (let i = 0; i < key.length; i++) h = ((h * 31) + key.charCodeAt(i)) >>> 0;
  return MODULE_PALETTE[h % MODULE_PALETTE.length];
}

// Resolve the per-link tint — every module gets its own colour; standalone
// routes hash from the path; the dashboard stays Overview-blue.
function tintForLink(to) {
  if (!to) return GROUP_TONE.Other.hex;
  if (to === "/") return GROUP_TONE.Overview.hex;
  if (to.startsWith("/m/")) return tintForKey(to.slice(3));
  return tintForKey(to.replace(/^\//, ""));
}

/** Group section header — slim coloured dot + uppercase label + thin slate line. */
function GroupHeader({ group, collapsed }) {
  const t = tone(group);
  if (collapsed) {
    return (
      <div className="flex items-center justify-center px-2 mb-2 mt-2" title={group}>
        <span className="w-5 h-px bg-slate-200" />
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 px-3 mb-1.5 mt-3 first:mt-0">
      <span className={`w-1.5 h-1.5 rounded-full ${t.dot}`} />
      <span className="text-[9.5px] uppercase tracking-[0.20em] font-semibold text-slate-400">{group}</span>
      <span className="flex-1 h-px bg-slate-200" />
    </div>
  );
}

/** A single sidebar nav link. Glowing icon, mirror sheen on hover, glassy depth on active. */
function DirectoryCounts({ collapsed }) {
  const [c, setC] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = () => api.get("/directory/today-counts").then(({ data }) => alive && setC(data)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  if (!c || collapsed) return null;
  return (
    <span className="relative z-10 ml-auto flex items-center gap-1" data-testid="sidebar-directory-counts">
      <span className="rounded-full bg-emerald-100 px-1.5 py-px text-[10px] font-bold text-emerald-700 mono" title={`${c.punched_in} punched in today`} data-testid="sidebar-directory-punched">{c.punched_in}</span>
      <span className="rounded-full bg-rose-100 px-1.5 py-px text-[10px] font-bold text-rose-700 mono" title={`${c.absent} absent today${c.on_leave ? ` · ${c.on_leave} on leave` : ""}`} data-testid="sidebar-directory-absent">{c.absent}</span>
    </span>
  );
}

function NavItem({ to, icon: Icon, children, group = "Overview", testid, end = false, collapsed = false, badge = null }) {
  // Each module gets its own deterministic vibrant tint; the group fallback only
  // applies to the group section dots/headers (not individual items anymore).
  const tint = tintForLink(to);
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testid}
      title={collapsed ? (typeof children === "string" ? children : "") : undefined}
      style={{ ["--nav-tint"]: tint }}
      className={({ isActive }) =>
        `pk-nav-item group relative flex items-center ${collapsed ? "justify-center px-2" : "gap-2.5 px-2.5"} py-2 my-0.5 text-[13px] rounded-lg overflow-hidden ${
          isActive ? "pk-nav-item-active" : ""
        }`
      }
    >
      {({ isActive }) => (
        <>
          {/* Active glowing left rail */}
          {isActive && <span className="pk-nav-rail absolute left-0 top-1/2 -translate-y-1/2 h-6 w-[3px] rounded-r-full" aria-hidden="true" />}
          {/* Mirror sheen — diagonal highlight sweeping across on hover */}
          <span className="pk-nav-mirror" aria-hidden="true" />

          <span className="pk-nav-icon relative z-10 flex items-center justify-center flex-shrink-0 w-7 h-7">
            <Icon size={16} strokeWidth={2} />
          </span>

          {!collapsed && (
            <>
              <span
                className={`relative z-10 flex-1 truncate transition-[transform,color] duration-200 ${
                  isActive ? "font-semibold pk-nav-text-active" : "font-medium text-slate-600 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-white group-hover:translate-x-0.5"
                }`}
              >
                {children}
              </span>
              {badge}
              {isActive && <span className="relative z-10 w-1.5 h-1.5 rounded-full pk-nav-dot" aria-hidden="true" />}
            </>
          )}

          {/* Tooltip pop-up shown only when collapsed and hovered */}
          {collapsed && (
            <span className="pointer-events-none absolute left-full ml-3 z-50 px-2.5 py-1 rounded-md bg-slate-900 text-white text-[11px] font-semibold whitespace-nowrap shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-150">
              {children}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // v107 — global Card / List preference broadcast to every module.
  const [globalVM, setGlobalVM] = useGlobalViewMode();
  // v109.9.86 — hide the toggle on routes where card/list has no meaning.
  const hideViewToggle = (() => {
    const p = location?.pathname || "";
    return p === "/" || p.startsWith("/my-attendance") || p.startsWith("/directory");
  })();
  // Persist collapsed state across sessions
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("pk_sidebar_collapsed") === "1"; } catch { return false; }
  });
  const toggleCollapsed = () => {
    setCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem("pk_sidebar_collapsed", next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };

  // Auto-close mobile drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Preserve sidebar scroll position across route changes so clicking
  // a menu item near the bottom doesn't snap us back to the top.
  const navScrollRef = useRef(null);
  useEffect(() => {
    const el = navScrollRef.current;
    if (!el) return;
    const saved = parseInt(sessionStorage.getItem("pk_sidebar_scroll") || "0", 10);
    if (!Number.isNaN(saved)) el.scrollTop = saved;
    const onScroll = () => sessionStorage.setItem("pk_sidebar_scroll", String(el.scrollTop));
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [user, collapsed]);
  // Re-apply saved scroll after every route change (so the new render keeps the same position)
  useEffect(() => {
    const el = navScrollRef.current;
    if (!el) return;
    const saved = parseInt(sessionStorage.getItem("pk_sidebar_scroll") || "0", 10);
    if (!Number.isNaN(saved)) el.scrollTop = saved;
  }, [location.pathname]);

  if (!user) return null;
  const isMaster = user.role === "master_admin";

  const SidebarBody = ({ collapsedView = false } = {}) => (
    <>
      {/* Brand header */}
      <div className={`${collapsedView ? "px-2" : "px-5"} py-4 border-b border-slate-200 bg-white relative`}>
        <div className="flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2.5 min-w-0" data-testid="sidebar-logo">
            <div className="w-9 h-9 rounded-lg bg-indigo-600 flex items-center justify-center text-white font-bold heading text-sm shadow-sm ring-1 ring-indigo-700 flex-shrink-0">
              PV
            </div>
            {!collapsedView && (
              <div className="min-w-0">
                <div className="font-bold text-[15px] leading-tight text-slate-900 tracking-tight">
                  Panchakosh
                </div>
                <div className="flex items-center gap-1 text-[9.5px] uppercase tracking-[0.18em] font-semibold text-slate-500">
                  <span className="w-1 h-1 rounded-full bg-indigo-500" />
                  Ventures Portal
                </div>
              </div>
            )}
          </Link>
          {/* Collapse toggle — desktop only */}
          <button
            onClick={toggleCollapsed}
            className="hidden lg:inline-flex shrink-0 w-7 h-7 rounded-md items-center justify-center text-slate-400 hover:text-indigo-700 hover:bg-slate-100 transition-colors"
            title={collapsedView ? "Expand sidebar" : "Collapse sidebar"}
            data-testid="sidebar-collapse-toggle"
          >
            {collapsedView ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
          </button>
        </div>
      </div>

      {/* Scrollable nav */}
      <nav ref={navScrollRef} className={`flex-1 overflow-y-auto ${collapsedView ? "px-2" : "px-3"} py-4 bg-white`}>
        <div>
          <GroupHeader group="Overview" collapsed={collapsedView} />
          <NavItem to="/" end icon={LayoutDashboard} group="Overview" testid="sidebar-dashboard-link" collapsed={collapsedView}>Dashboard</NavItem>
          <NavItem to="/my-attendance" icon={Clock} group="Overview" testid="sidebar-my-attendance-link" collapsed={collapsedView}>My Attendance</NavItem>
        </div>

        <div>
          <GroupHeader group="Workspace" collapsed={collapsedView} />
          <NavItem to="/directory" icon={BookUser} group="Workspace" testid="sidebar-directory-link" collapsed={collapsedView} badge={<DirectoryCounts collapsed={collapsedView} />}>Directory</NavItem>
          <NavItem to="/tasks" icon={ListChecks} group="Workspace" testid="sidebar-tasks-link" collapsed={collapsedView}>Tasks</NavItem>
          <NavItem to="/my-assets" icon={Laptop} group="Workspace" testid="sidebar-my-assets-link" collapsed={collapsedView}>My Assets</NavItem>
          <NavItem to="/my-sales" icon={TrendingUp} group="Workspace" testid="sidebar-my-sales-link" collapsed={collapsedView}>My Sales</NavItem>
        </div>

        {isMaster && (
          <div>
            <GroupHeader group="Administration" collapsed={collapsedView} />
            <NavItem to="/admin-hub" icon={ShieldCheck} group="Administration" testid="sidebar-admin-hub-link" collapsed={collapsedView}>Admin Hub</NavItem>
          </div>
        )}

        {MODULE_GROUPS.map((group) => {
          const items = Object.entries(MODULE_CATALOG).filter(
            ([k, m]) => m.group === group && !m.hiddenInSidebar && moduleAccessibleFor(user, k)
          );
          if (items.length === 0) return null;
          return (
            <div key={group}>
              <GroupHeader group={group} collapsed={collapsedView} />
              {items.map(([key, mod]) => (
                <NavItem
                  key={key}
                  to={mod.customRoute || `/m/${key}`}
                  icon={mod.icon}
                  group={group}
                  testid={`sidebar-${key}-link`}
                  collapsed={collapsedView}
                >
                  {mod.label}
                </NavItem>
              ))}
            </div>
          );
        })}
      </nav>

      {/* Footer — user profile pill */}
      <div className={`border-t border-slate-200 ${collapsedView ? "p-2" : "p-3"} bg-white`}>
        <DropdownMenu>
          <DropdownMenuTrigger
            className={`w-full flex items-center ${collapsedView ? "justify-center" : "gap-2.5 px-2"} py-2 rounded-lg hover:bg-slate-50 transition-colors`}
            title={collapsedView ? user.name : undefined}
            data-testid="user-menu-trigger"
          >
            <div className="w-9 h-9 rounded-full bg-indigo-600 text-white grid place-items-center text-[13px] font-bold ring-2 ring-white shadow-sm flex-shrink-0 overflow-hidden">
              {user.photo_gridfs_id ? (
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/users/${user.id}/photo?v=${user.photo_gridfs_id}`}
                  alt={user.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                user.name?.charAt(0).toUpperCase() || "U"
              )}
            </div>
            {!collapsedView && (
              <>
                <div className="flex-1 text-left min-w-0">
                  <div className="text-[13px] font-semibold text-slate-900 leading-tight truncate">{user.name}</div>
                  <div className="text-[10.5px] text-slate-500 truncate">{ROLE_LABELS[user.role] || user.role}</div>
                </div>
                <ChevronDown size={14} strokeWidth={1.5} className="text-slate-400 shrink-0" />
              </>
            )}
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuLabel className="text-xs">
              <div className="font-semibold text-slate-900">{user.name}</div>
              <div className="truncate mono">{user.email}</div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => replayOnboardingTour(navigate)} data-testid="menu-replay-tour">
              <PlayCircle size={14} strokeWidth={1.5} className="mr-2" /> Replay tour
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => { logout(); navigate("/login"); }} data-testid="menu-logout">
              <LogOut size={14} strokeWidth={1.5} className="mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );

  const sidebarWidth = collapsed ? "w-16" : "w-64";
  const mainOffset = collapsed ? "lg:ml-16" : "lg:ml-64";

  return (
    <div className="min-h-screen flex bg-[var(--pk-bg)]">
      {/* Desktop sidebar */}
      <aside
        className={`hidden lg:flex ${sidebarWidth} border-r border-slate-200/70 bg-white dark:bg-slate-900 flex-col fixed inset-y-0 left-0 z-30 transition-[width] duration-200 ease-out`}
        data-testid="desktop-sidebar"
        data-collapsed={collapsed ? "true" : "false"}
      >
        {SidebarBody({ collapsedView: collapsed })}
      </aside>

      {/* Mobile drawer (always full label view) */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="left" className="w-72 p-0 lg:hidden flex flex-col" data-testid="mobile-sidebar-drawer">
          {SidebarBody({ collapsedView: false })}
        </SheetContent>
      </Sheet>

      {/* Main column */}
      <main className={`flex-1 ${mainOffset} min-h-screen flex flex-col transition-[margin] duration-200 ease-out`}>
        <header className="hidden lg:flex sticky top-0 z-20 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-slate-200/70 items-center justify-end gap-3 px-6 py-2" data-testid="desktop-top-bar">
          <DailyStatusPills />
          <TicketBookingBell />
          {!hideViewToggle && (
            <ViewModeToggle mode={globalVM} onChange={setGlobalVM} testid="global-view-mode" />
          )}
          <ThemeToggle />
          <AnnouncementsBell />
          <MasterAdminEditRequestsButton />
          <NotificationBell />
        </header>
        <header className="lg:hidden sticky top-0 z-20 bg-white dark:bg-slate-900 border-b border-slate-200/70 flex items-center justify-between px-3 py-2.5">
          <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
            <SheetTrigger asChild>
              <button className="p-2 rounded-md hover:bg-slate-100 active:bg-slate-200" data-testid="mobile-menu-button" aria-label="Open menu">
                <Menu size={20} strokeWidth={1.75} />
              </button>
            </SheetTrigger>
          </Sheet>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-gradient-to-br from-blue-600 via-indigo-700 to-violet-800 flex items-center justify-center text-white font-bold heading text-[11px] shadow-sm">PV</div>
            <span className="heading font-bold text-[14px] text-slate-900">Panchakosh</span>
          </Link>
          <div className="flex items-center gap-1.5">
            <DailyStatusPills />
            <TicketBookingBell />
            {!hideViewToggle && (
              <ViewModeToggle mode={globalVM} onChange={setGlobalVM} testid="global-view-mode-mobile" compact />
            )}
            <ThemeToggle />
            <AnnouncementsBell />
            <MasterAdminEditRequestsButton />
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-600 via-indigo-700 to-violet-800 text-white grid place-items-center text-[12px] font-semibold ring-2 ring-white shadow-sm overflow-hidden" data-testid="mobile-avatar">
                {user.photo_gridfs_id ? (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/users/${user.id}/photo?v=${user.photo_gridfs_id}`}
                    alt={user.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  user.name?.charAt(0).toUpperCase() || "U"
                )}
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel className="text-xs">
                  <div className="font-semibold text-slate-900">{user.name}</div>
                  <div className="truncate mono">{user.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => replayOnboardingTour(navigate)}>
                  <PlayCircle size={14} strokeWidth={1.5} className="mr-2" /> Replay tour
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { logout(); navigate("/login"); }}>
                  <LogOut size={14} strokeWidth={1.5} className="mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-7 fade-in">{children}</div>
        <NotificationHighlighter />
      </main>
      {/* MandatoryPasswordChange is rendered by ProtectedRoute so it covers
          custom routes (e.g. /my-onboarding) that skip the Layout shell. */}
      {/* First-login tutorial — auto-runs once, replayable from profile menu */}
      <OnboardingTour />
      {/* Mandatory company-policy acknowledgement banner + blocking modal */}
      <PolicyAckGate />
      {/* Per-user active/idle time tracker — pings /api/activity/heartbeat */}
      <ActivityHeartbeat />
      {/* Floating chat bubble — auto-pops on new messages */}
      <FloatingChatBubble />
      {/* Mandatory Important-Announcement banner (5-login enforcement, 1-min timer) */}
      <MandatoryAnnouncementBanner />
      {/* Non-important announcements are NOT auto-shown — the top-bar
          megaphone bell surfaces them on click, per product spec. */}
    </div>
  );
}
